import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Button,
  Text,
  Avatar,
  Tooltip,
} from '@fluentui/react-components';
import type { FluentIcon } from '@fluentui/react-icons';
import { transformrBrand, brandFonts, sidebarTokens } from '@/theme/transformrTheme';
import {
  SignOutRegular,
  HomeRegular,
  DocumentBulletListRegular,
  ShieldCheckmarkRegular,
  ShieldLockRegular,
  CalendarRegular,
  PanelLeftContractRegular,
  PanelLeftExpandRegular,
} from '@fluentui/react-icons';
import { useAuth } from '@/hooks/useAuth';
import { useDeepScanEligibility } from '@/hooks/useDeepScanEligibility';

/* App-shell chrome — 100% the DevOpsReports TeamsAppLayout LHS menu (collapsible
   260px rail → brand → nav → user footer), dark navy surface like the other
   apps, with the green brand accent for active nav. The main column is fixed to
   the viewport height: the breadcrumb stays pinned and content scrolls beneath. */

const useStyles = makeStyles({
  root: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // 260px expanded / 56px collapsed — rem layout dims (no spacing token that large).
  sidebar: {
    width: '16.25rem',
    backgroundColor: sidebarTokens.surface,
    color: sidebarTokens.text,
    fontFamily: brandFonts.body,
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${sidebarTokens.border}`,
    flexShrink: 0,
    zIndex: 2,
    boxShadow: tokens.shadow16,
    position: 'sticky',
    top: 0,
    height: '100vh',
    transitionProperty: 'width',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
  },
  sidebarCollapsed: {
    width: '3.5rem',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXXL}`,
    borderBottom: `1px solid ${sidebarTokens.border}`,
  },
  headerCollapsed: {
    padding: `${tokens.spacingVerticalL} 0`,
    justifyContent: 'center',
  },
  brandStack: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  brandName: {
    color: sidebarTokens.textStrong,
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightBold,
    lineHeight: tokens.lineHeightBase400,
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
  },
  brandSub: {
    color: sidebarTokens.muted,
    fontFamily: brandFonts.heading,
    fontSize: '0.5625rem', // 9px — below the smallest fontSize token
    fontWeight: tokens.fontWeightMedium,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
  },
  logoIcon: {
    height: '1.6875rem', // 27px
    width: 'auto',
    flexShrink: 0,
    display: 'block',
  },
  collapseToggle: {
    marginLeft: 'auto',
    color: sidebarTokens.text,
    ':hover': { color: sidebarTokens.textStrong, backgroundColor: sidebarTokens.hover },
  },
  collapseToggleCollapsed: {
    marginLeft: 0,
    color: sidebarTokens.text,
    ':hover': { color: sidebarTokens.textStrong, backgroundColor: sidebarTokens.hover },
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalS}`,
    gap: tokens.spacingVerticalXS,
    flexGrow: 1,
    overflowY: 'auto',
  },
  navGroupLabel: {
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase100,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: sidebarTokens.muted,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL} ${tokens.spacingVerticalXS}`,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalMNudge} ${tokens.spacingHorizontalL}`,
    textDecoration: 'none',
    color: sidebarTokens.text,
    fontWeight: tokens.fontWeightMedium,
    borderRadius: tokens.borderRadiusMedium,
    transitionProperty: 'all',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
    ':hover': {
      backgroundColor: sidebarTokens.hover,
      color: sidebarTokens.textStrong,
    },
  },
  navItemCollapsed: {
    justifyContent: 'center',
    paddingLeft: 0,
    paddingRight: 0,
  },
  navItemActive: {
    backgroundColor: sidebarTokens.active,
    color: sidebarTokens.textStrong,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': {
      backgroundColor: sidebarTokens.active,
      color: sidebarTokens.textStrong,
    },
  },
  footer: {
    padding: tokens.spacingHorizontalL,
    borderTop: `1px solid ${sidebarTokens.border}`,
  },
  footerCollapsed: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
    paddingLeft: 0,
    paddingRight: 0,
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
  },
  userDetails: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    flex: 1,
  },
  userName: {
    color: sidebarTokens.textStrong,
    fontWeight: tokens.fontWeightSemibold,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  userMail: {
    color: sidebarTokens.muted,
    fontSize: tokens.fontSizeBase100,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  signOut: {
    width: '100%',
    justifyContent: 'flex-start',
    color: sidebarTokens.text,
    ':hover': { color: sidebarTokens.textStrong, backgroundColor: sidebarTokens.hover },
  },
  signOutCollapsed: {
    width: 'auto',
    color: sidebarTokens.text,
    ':hover': { color: sidebarTokens.textStrong, backgroundColor: sidebarTokens.hover },
  },
  // Main column — fixed to viewport height; breadcrumb pinned, content scrolls.
  main: {
    flexGrow: 1,
    minWidth: 0,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    zIndex: 3,
    overflow: 'hidden',
  },
  crumbs: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXXL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${transformrBrand.grey200}`,
    fontFamily: brandFonts.body,
    fontSize: tokens.fontSizeBase300,
  },
  crumbLink: {
    color: transformrBrand.green,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 0,
    fontFamily: brandFonts.body,
    fontSize: tokens.fontSizeBase300,
    ':hover': { textDecoration: 'underline' },
  },
  crumbCurrent: {
    color: transformrBrand.textDark,
    fontWeight: tokens.fontWeightSemibold,
  },
  crumbSep: {
    color: transformrBrand.grey300,
  },
  mainScroll: {
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: tokens.spacingHorizontalXXL,
  },
  contentInner: {
    width: '100%',
  },
});

interface NavItem {
  path: string;
  label: string;
  icon: FluentIcon;
}

export function MainLayout() {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isEligible: deepScanEnabled } = useDeepScanEligibility();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('readiness.sidebar.collapsed') === 'true'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('readiness.sidebar.collapsed', String(collapsed)); }
    catch { /* ignore */ }
  }, [collapsed]);

  const navItems: NavItem[] = [
    { path: '/', label: 'AI Readiness Report', icon: HomeRegular },
    { path: '/book', label: 'Book a Meeting', icon: CalendarRegular as FluentIcon },
  ];
  if (deepScanEnabled) {
    navItems.push({ path: '/deep-analysis', label: 'Deep Analysis', icon: ShieldLockRegular as FluentIcon });
  }
  navItems.push(
    { path: '/reports', label: 'My Reports', icon: DocumentBulletListRegular },
    { path: '/data-privacy', label: 'Your Data', icon: ShieldCheckmarkRegular as FluentIcon },
  );

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
  const activeItem = navItems.find((i) => isActive(i.path));
  const crumbCurrent =
    location.pathname === '/' ? 'Generate' : activeItem?.label ?? 'Readiness';

  const handleLogout = async () => {
    try { await logout(); }
    catch (error) { console.error('Logout failed:', error); }
  };

  return (
    <div className={styles.root}>
      <aside className={mergeClasses(styles.sidebar, collapsed && styles.sidebarCollapsed)}>
        <div className={mergeClasses(styles.header, collapsed && styles.headerCollapsed)}>
          {!collapsed && (
            <>
              <img src="/assets/logos/transformr-t-logo.svg" alt="Transformr" className={styles.logoIcon} />
              <div className={styles.brandStack}>
                <Text as="span" className={styles.brandName}>AI Readiness</Text>
                <span className={styles.brandSub}>Transformr</span>
              </div>
            </>
          )}
          <Tooltip content={collapsed ? 'Expand menu' : 'Collapse menu'} relationship="label" positioning="after">
            <Button
              appearance="subtle"
              className={collapsed ? styles.collapseToggleCollapsed : styles.collapseToggle}
              icon={collapsed ? <PanelLeftExpandRegular /> : <PanelLeftContractRegular />}
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            />
          </Tooltip>
        </div>

        <nav className={styles.nav}>
          {!collapsed && <span className={styles.navGroupLabel}>Reports</span>}
          {navItems.map((item) => {
            const link = (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={mergeClasses(
                  styles.navItem,
                  collapsed && styles.navItemCollapsed,
                  isActive(item.path) && styles.navItemActive,
                )}
              >
                <item.icon fontSize={20} />
                {!collapsed && <Text>{item.label}</Text>}
              </NavLink>
            );
            return collapsed ? (
              <Tooltip key={item.path} content={item.label} relationship="label" positioning="after">
                {link}
              </Tooltip>
            ) : link;
          })}
        </nav>

        {user && (
          <div className={mergeClasses(styles.footer, collapsed && styles.footerCollapsed)}>
            {collapsed ? (
              <Tooltip content={user.displayName} relationship="label" positioning="after">
                <Avatar name={user.displayName} color="brand" size={32} />
              </Tooltip>
            ) : (
              <div className={styles.userInfo}>
                <Avatar name={user.displayName} color="brand" size={36} />
                <div className={styles.userDetails}>
                  <Text size={200} truncate className={styles.userName}>{user.displayName}</Text>
                  <span className={styles.userMail}>{user.email}</span>
                </div>
              </div>
            )}
            {collapsed ? (
              <Tooltip content="Sign out" relationship="label" positioning="after">
                <Button
                  appearance="subtle"
                  className={styles.signOutCollapsed}
                  icon={<SignOutRegular />}
                  onClick={handleLogout}
                  aria-label="Sign out"
                />
              </Tooltip>
            ) : (
              <Button
                appearance="subtle"
                className={styles.signOut}
                icon={<SignOutRegular />}
                onClick={handleLogout}
              >
                Sign Out
              </Button>
            )}
          </div>
        )}
      </aside>

      <main className={styles.main}>
        <div className={styles.crumbs}>
          <button className={styles.crumbLink} onClick={() => navigate('/')}>Home</button>
          <span className={styles.crumbSep}>›</span>
          {location.pathname === '/' ? (
            <>
              <button className={styles.crumbLink} onClick={() => navigate('/')}>AI Readiness Report</button>
              <span className={styles.crumbSep}>›</span>
              <Text className={styles.crumbCurrent}>{crumbCurrent}</Text>
            </>
          ) : (
            <Text className={styles.crumbCurrent}>{crumbCurrent}</Text>
          )}
        </div>
        <div className={styles.mainScroll}>
          <div className={styles.contentInner}>
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
